/**
 * Estate Flow - Property Management Platform
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 * 
 * This software and associated documentation files (the "Software") are the 
 * proprietary property of Sebastian Alliance Group LLC and are protected by 
 * copyright, trademark, and other intellectual property laws.
 * 
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium, is strictly prohibited without express written permission
 * from Sebastian Alliance Group LLC.
 * 
 * For licensing inquiries: legal@sebastianalliancegroup.com
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import HOAAdminDashboard from './HOAAdminDashboard';
import HOAOwnerDashboard from './HOAOwnerDashboard';
import LandlordDashboard from './LandlordDashboard';
import PropertyManagerDashboard from './PropertyManagerDashboard';
import TenantDashboard from './TenantDashboard';
import AdminDashboard from './AdminDashboard';
import BillingLockScreen from './BillingLockScreen';
import VacationRentalsDashboard from './VacationRentalsDashboard';

const DashboardRouter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [trialLocked, setTrialLocked] = useState(false);
  const [trialData, setTrialData] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/');
        return;
      }

      const response = await api.get('/users/me');
      const userData = response.data;
      setUser(userData);

      // Check trial status for non-admin users
      const orgId = userData.org_id || userData.organization_id || userData.organization?.id;
      if (orgId && userData.role !== 'admin') {
        try {
          const trialRes = await api.get(`/organizations/${orgId}/trial-status`);
          const trial = trialRes.data;
          if (trial.account_locked || (trial.billing_required && !trial.has_card_on_file)) {
            setTrialData({ ...trial, org_id: orgId });
            setTrialLocked(true);
          }
        } catch (e) {
          // Trial check failed — don't block access
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('access_token');
      navigate('/');
    }
  };

  // Use useEffect for navigation to avoid "Cannot update BrowserRouter while rendering" error
  useEffect(() => {
    if (!loading && user && user.role) {
      // Check if we need to redirect based on role
      const currentPath = window.location.pathname;
      
      // Check if user is a vacation rental manager (landlord role with vacation_rental org_type)
      const isVacationRental = user.role === 'landlord' && 
        (user.org_type === 'vacation_rental' || 
         user.organization?.type === 'vacation_rental');
      
      // Redirect vacation rental managers to their specialized dashboard
      if (isVacationRental) {
        if (!currentPath.includes('/vacation-rentals')) {
          navigate('/vacation-rentals', { replace: true });
        }
        return;
      }
      
      switch (user.role) {
        case 'admin':
          if (!currentPath.includes('/admin/dashboard')) {
            navigate('/admin/dashboard', { replace: true });
          }
          break;
        case 'platform_super_admin':
        case 'landlord':
          if (user.role === 'platform_super_admin') {
            // Platform owner gets HOA dashboard with full access
            if (!currentPath.includes('/hoa-admin/dashboard')) {
              navigate('/hoa-admin/dashboard', { replace: true });
            }
            break;
          }
          if (!currentPath.includes('/landlord/dashboard')) {
            navigate('/landlord/dashboard', { replace: true });
          }
          break;
        case 'hoa':
        case 'hoa_admin':
          if (!currentPath.includes('/hoa-admin/dashboard')) {
            navigate('/hoa-admin/dashboard', { replace: true });
          }
          break;
        case 'hoa_owner':
          if (!currentPath.includes('/hoa-owner/dashboard')) {
            navigate('/hoa-owner/dashboard', { replace: true });
          }
          break;
        case 'property_manager':
          if (!currentPath.includes('/property-manager/dashboard')) {
            navigate('/property-manager/dashboard', { replace: true });
          }
          break;
        case 'tenant':
          if (!currentPath.includes('/tenant/dashboard')) {
            navigate('/tenant/dashboard', { replace: true });
          }
          break;
        default:
          navigate('/');
      }
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!user || !user.role) {
    return null;
  }

  // Show billing lock screen if trial expired and no card on file
  if (trialLocked && trialData) {
    return <BillingLockScreen orgId={trialData.org_id} trialData={trialData} onUnlocked={() => { setTrialLocked(false); fetchUser(); }} />;
  }

  // Render appropriate dashboard based on user role
  // Check for vacation rental first (landlord with vacation_rental org_type)
  const isVacationRental = user.role === 'landlord' && 
    (user.org_type === 'vacation_rental' || 
     user.organization?.type === 'vacation_rental');
  
  if (isVacationRental) {
    return <VacationRentalsDashboard />;
  }
  
  switch (user.role) {
    case 'admin':
      return <AdminDashboard />;
    case 'platform_super_admin':
      return <HOAAdminDashboard />;
    case 'landlord':
      return <LandlordDashboard />;
    case 'hoa':
    case 'hoa_admin':
      return <HOAAdminDashboard />;
    case 'hoa_owner':
      return <HOAOwnerDashboard />;
    case 'property_manager':
      return <PropertyManagerDashboard />;
    case 'tenant':
      return <TenantDashboard />;
    default:
      return null;
  }
};

export default DashboardRouter;
